import { environment } from './../../../../environments/environment';
import { Component, OnInit, ViewChild, inject } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { SharedService } from '@shared/services/shared.service';
import { AddressComponent } from '../../reusable/address/address.component';
import { IHemoTechLicense } from 'app/models/licenses/hemo-tech/hemo-tech-license';
import { HemoTechLicenseService } from 'app/services/license/hemo-tech/hemo-tech-license.service';
import { StepperSelectionEvent } from '@angular/cdk/stepper';
import { ApplicantInformationComponent } from '../../reusable/applicant-information/applicant-information.component';
import { EmployerComponent } from '../hemo-tech/employer/employer.component';
import { QuestionsComponent } from './question/question.component';
import { CertificationComponent } from './certification/certification.component';
import { take } from 'rxjs';
import { ILicenseRequest } from 'app/models/licenses/license-request';
import { HemotechLicenseValidator } from '@shared/validators/hemotech-license-validator';
import { AuthService } from '@shared/auth/auth.service';
import { HemoTechLicenseExportComponent } from './hemo-tech-license-export/hemo-tech-license-export.component';

@Component({
  selector: 'app-hemo-tech',
  templateUrl: './hemo-tech.component.html',
  styleUrls: ['./hemo-tech.component.scss']
})

export class HemoTechComponent implements OnInit {
  @ViewChild(AddressComponent) addresses!: AddressComponent;
  @ViewChild(ApplicantInformationComponent) applicantInfo!: ApplicantInformationComponent;
  @ViewChild(EmployerComponent) employers!: EmployerComponent;
  @ViewChild(QuestionsComponent) questions!: QuestionsComponent;
  @ViewChild(CertificationComponent) certification!: CertificationComponent;

  public environment = environment;
  public showClaimLicense: boolean = false;
  public classification: string = '';
  public isParent: boolean = true;
  public validationErrors: string[] = [];
  public parentId: number = 0;
  public parentGuid: string = '';
  public parentName: string = '';
  public showSubmissionConfirmation: boolean = false;
  public isNewLicense: boolean = false;
  public editing: boolean = false;
  public applicantInfoValid: boolean = false;
  public employerInfoValid: boolean = false;
  public questionsValid: boolean = false;
  public certificationValid: boolean = false;
  public numberOfDaysLate: number = 0;
  public expirationDates: { [key: number]: Date | null } = {};

  public licenses: IHemoTechLicense[] = [];
  public license: IHemoTechLicense = {
    id: 0,
    userId: '',
    licenseId: '',
    enteredByStaff: false,
    assignedToId: '',
    assignedToName: '',
    finalApprovalBy: '',
    canRenew: false,
    isRenewal: false,
    electronicSignature: '',
    signedDate: '',
    applicationDate: '',
    approvedDate: '',
    deniedDate: '',
    expirationDate: '',
    closedDate: '',
    backgroundCheckDate: '',
    backgroundCheckCompleted: false,
    renewalVerification: '',
    similarity: 0,
    archived: false,
    licenseMasterId: 0,
    education: '',
    provisional: false,
    full: null,
    provisionalToFull: false,
    nationalCertification: '',
    nationalCertificationDate: '',
    initialCertificationDate: '',
    renewalCount: 0,
    adminPrintTitle: '',
    applicantFirstName: '',
    applicantLastName: '',
    applicantPhone: '',
    applicantEmail: '',
    primaryApproverName: '',
    primaryApproved: false,
    primaryInitials: '',
    primaryApprovalDate: '',
    reason: '',
    statuses: []
  };

  public licenseForm = new FormGroup({
    full: new FormControl<boolean | null>(null, Validators.required),
    nationalCertification: new FormControl(''),
    approvedDate: new FormControl<Date | null>(null),
    education: new FormControl(''),
  });

  public licenseRequests: ILicenseRequest[] = [];
  public licenseRequest: ILicenseRequest = {
    id: 0,
    owner: '',
    licenseNumber: '',
    entityType: '',
    ownerChange: false,
    isSubunit: false,
    parentId: '',
    email: '',
    requestData: '',
    signature: '',
    dateSigned: '',
    approved: false,
    denied: false,
    deniedReason: '',
    finalDeterminationBy: '',
    finalDeterminationDate: ''
  }

  public licenseValidator = inject(HemotechLicenseValidator);

  public requestForm = new FormGroup({
    owner: new FormControl('', Validators.required),
    requestData: new FormControl('', Validators.required),
    licenseNumber: new FormControl('', {
      asyncValidators: [this.licenseValidator.validate.bind(this.licenseValidator)],
      updateOn: 'blur'
    })
  });

  public signatureForm = new FormGroup({
    electronicSignature: new FormControl('', Validators.required),
    signedDate: new FormControl<Date | null>(null, Validators.required)
  });

  public authService = inject(AuthService);
  public sharedService = inject(SharedService);
  public licenseService = inject(HemoTechLicenseService);
  public router = inject(Router);
  public dialog = inject(MatDialog);

  ngOnInit(): void {
    this.getLicenses();
    this.sharedService.setUserTheme();
  }

  getLicenses(): void {
    this.licenseService.getLicenses().subscribe({
      next: response => (this.licenses = response),
      error: e => this.sharedService.toastr.error(e),
      complete: () => {
        this.getLicenseRequests();
        this.licenses.every(l => l.archived) ||
        this.licenses.every(l => l.statuses.some(s => s.label === 'Expired' || s.label === 'Denied'));
        if(!this.environment.production){
          this.licenses.forEach(license => {
            this.expirationDates[license.id] = null;
          });
        }
      }
    });
  }

  getLicenseRequests(): void {
    this.licenseService.getLicenseRequests().subscribe({
      next: response => this.licenseRequests = response,
      error: e => {
        console.log('error getting license requests', e);
        this.sharedService.toastr.error('Error getting license requests');
      },
      complete: () => {
        this.showClaimLicense = this.licenses.length === 0 &&
                                (this.licenseRequests.length === 0 || this.licenseRequests.every(r => r.denied));
      }
    });
  }

  createLicenseRequest(): void {
    let form = this.requestForm.getRawValue();
    this.licenseRequest.owner = form.owner;
    this.licenseRequest.requestData = form.requestData;
    this.licenseRequest.email = this.authService.email;
    this.licenseRequest.licenseNumber = form.licenseNumber;

    this.licenseService.requestLicense(this.licenseRequest).subscribe({
      next: response => this.licenseRequest.id = response,
      complete: () => {
        this.sharedService.toastr.success('License request submitted successfully.');
        this.licenseRequests.push(this.licenseRequest);
        this.showClaimLicense = this.licenses.length === 0 &&
                                (this.licenseRequests.length === 0 || this.licenseRequests.every(r => r.denied));
        this.requestForm.reset();
      },
      error: e => {
        console.log('error requesting license', e);
        this.sharedService.toastr.error('Error requesting license');
      }
    });
  }

  cancelLicenseRequest(request: ILicenseRequest): void {
    this.sharedService.openConfirm("Cancel this license request?");
    this.sharedService.confirmed().subscribe(
      confirmed => {
        if(confirmed){
          this.licenseService.cancelLicenseRequest(request.id).subscribe({
            complete: () => {
              this.sharedService.toastr.success('License request canceled successfully.');
              this.licenseRequests = this.licenseRequests.filter(r => r.id != request.id);
              this.showClaimLicense = this.licenses.length === 0 &&
                                      (this.licenseRequests.length === 0 || this.licenseRequests.every(r => r.denied));
            },
            error: e => {
              console.log('error canceling license request', e);
              this.sharedService.toastr.error('Error canceling license request');
            }
          });
        }
      }
    );
  }

  newLicense(): void {
    this.clearValidationErrors();
    this.editing = true;
    this.isNewLicense = true;
    this.license = this.getEmptyLicense();
    this.license.id = 0;
    this.license.full = this.hasProvisional;
    this.updateLicenseForm(this.license);
    this.licenses.push(this.license);
  }

  getEmptyLicense(): IHemoTechLicense {
    return {
      id: 0,
      userId: '',
      licenseId: '',
      enteredByStaff: false,
      assignedToId: '',
      assignedToName: '',
      finalApprovalBy: '',
      canRenew: false,
      isRenewal: false,
      electronicSignature: '',
      signedDate: '',
      applicationDate: '',
      approvedDate: '',
      deniedDate: '',
      expirationDate: '',
      closedDate: '',
      backgroundCheckDate: '',
      backgroundCheckCompleted: false,
      renewalVerification: '',
      similarity: 0,
      archived: false,
      licenseMasterId: 0,
      education: '',
      provisional: false,
      full: null,
      provisionalToFull: false,
      nationalCertification: '',
      nationalCertificationDate: '',
      initialCertificationDate: '',
      renewalCount: 0,
      adminPrintTitle: '',
      applicantFirstName: '',
      applicantLastName: '',
      applicantPhone: '',
      applicantEmail: '',
      primaryApproverName: '',
      primaryApproved: false,
      primaryInitials: '',
      primaryApprovalDate: '',
      reason: '',
      statuses: []
    }
  }

  updateLicenseForm(license: IHemoTechLicense): void {
    this.licenseForm.reset({
      full: license.full,
      education: license.education,
      nationalCertification: license.nationalCertification
    });
    this.signatureForm.reset({
      electronicSignature: license.electronicSignature
    });

    if(license.isRenewal || this.hasProvisional){
      this.licenseForm.get('full')?.disable();
    }

    if(license.approvedDate){
      this.licenseForm.get('approvedDate')?.setValue(new Date(license.approvedDate));
    }

    if (license.signedDate) {
      this.signatureForm.get('signedDate')?.setValue(new Date(license.signedDate));
    }
  }

  saveLicense(): void {
    // Create only if this license hasn't been created yet
    if (this.license.id === 0) {
      this.createLicense();
      return;
    }
    else{
      this.updateLicense();
    }
  }

  createLicense(): void {
    const isFull: boolean = this.licenseForm.get('full')?.value ?? false;
    this.licenseService.createLicense(isFull).subscribe({
      next: license => {
        this.license = license;
        this.licenses.push(license);
      },
      error: e => {
        this.sharedService.toastr.error('Error Creating Application: ' + e);
      },
      complete: () => {
        this.editing = true;
        this.isNewLicense = false;
      }
    });
  }

  updateLicense(): void {
    const isFull: boolean = this.licenseForm.get('full')?.value ?? false;
    this.license.full = isFull;
    this.licenseService.updateLicense(this.license).subscribe({
      error: e => {
        this.sharedService.toastr.error('Error Updating Application');
        console.log('updating application error', e);
      }
    });
  }

  selectLicense(license: IHemoTechLicense): void {
    this.license = license;
    this.editing = true;
    if(this.license.isRenewal){
      const currentLicense: IHemoTechLicense = this.licenses.find(l => l.statuses.some(s => s.label === 'Approved' && l.licenseId === license.licenseId))!;
      const expirationDate = new Date(currentLicense.expirationDate!);
      const now = this.sharedService.today;
      this.numberOfDaysLate = Math.ceil((now.getTime() - expirationDate.getTime()) / (1000 * 3600 * 24));
    }
    this.updateLicenseForm(license);
  }

  closeLicense(skipSave: boolean = false): void {
    if (this.license.id === 0) {
      this.licenses = this.licenses.filter(l => l.id !== 0);
      this.editing = false;
      this.isNewLicense = false;
      this.license = this.getEmptyLicense();
      this.updateLicenseForm(this.license);
      this.getLicenses();
      return;
    }

    if (!this.locked && this.applicantInfo) {
      this.applicantInfo.saveApplicantInfo();
    }

    if (!this.locked && this.certification) {
      this.certification.saveCertification();
    }

    if (!skipSave) {
      this.saveLicense();
    }

    this.editing = false;
    this.isNewLicense = false;
    this.license = this.getEmptyLicense();
    this.updateLicenseForm(this.license);
    this.getLicenses();
  }

  submitLicense(): void {
    if (!this.locked) {
      let form = this.signatureForm.value;
      this.license.electronicSignature = form.electronicSignature ?? '';
      this.license.signedDate = form.signedDate?.toDateString() ?? '';

      this.licenseService.submitLicense(this.license).subscribe({
        error: e => {
          console.log('error', e);
          this.sharedService.toastr.error("Could Not Submit Application");
        },
        complete: () => {
          this.sharedService.toastr.success("Application Submitted");
          let licenseIndex: number = this.licenses.findIndex(l => l.id === this.license.id);
          this.licenses[licenseIndex].statuses = this.licenses[licenseIndex].statuses.filter(s => s.label !== 'Started');
        }
      });
    }
  }

  renewLicense(license: IHemoTechLicense, provisionalToFull: boolean = false): void {
    this.sharedService.openConfirm('Renew this license?');
    this.sharedService.confirmed().pipe(take(1))
      .subscribe(confirmed => {
        if (!confirmed) return;
        const expirationDate = new Date(license.expirationDate!);
        const threeWeeksBeforeExpiration = new Date(expirationDate.getTime() - 21 * 24 * 60 * 60 * 1000);
        const isLate = (this.sharedService.today >= threeWeeksBeforeExpiration && this.isApproved(license) && license.expirationDate !== null);

        this.licenseService.renewLicense(license.id, provisionalToFull, isLate).subscribe({
          next: (renewedLicense) => {
            this.licenses.push(renewedLicense);
            this.selectLicense(renewedLicense);
          },
          error: e => {
            console.log('error renewing license', e);
            this.sharedService.toastr.error('Error renewing license');
          }
        });
      }
    );
  }

  changeStep(step: StepperSelectionEvent): void {
    if (!this.locked) {
      if (step.previouslySelectedIndex === 0) {
        this.saveLicense();
      }
      if (step.previouslySelectedIndex === 1) {
        this.applicantInfo.saveApplicantInfo();
      }
      if (step.previouslySelectedIndex === 3) {
        this.questions.saveQuestions();
      }
      if (step.previouslySelectedIndex === 4) {
        this.certification.certificationInfoForm.markAllAsTouched();
        this.certification.saveCertification();
      }
      if (step.selectedIndex === 5) {
        this.clearValidationErrors();
        this.validateApplicationInformation();
        this.validateEmployers();
        this.validateQuestionsComponent();
        this.validateCertification();
      }
    }
  }

  public get locked(): boolean {
    if (this.license.id === 0) {
      return false;
    }
    return !this.license.statuses.some(s => s.label === this.sharedService.started);
  }

  public get approved(): boolean {
    return this.license.statuses.some(s => s.label === this.sharedService.approved);
  }

  public get hasProvisional(): boolean {
    return this.licenses.some(l => !l.full && l.statuses.some(s => s.label === this.sharedService.approved));
  }

  public get hasRenewal(): boolean {
    return this.licenses.some(l => l.isRenewal);
  }

  public isApproved(license: IHemoTechLicense): boolean {
    return license.statuses.some(s => s.label === this.sharedService.approved);
  }

  public isExpired(license: IHemoTechLicense): boolean {
    return license.statuses.some(s => s.label === this.sharedService.expired);
  }

  public expiredGreaterThanAYear(license: IHemoTechLicense): boolean {
    const expirationDate = new Date(license.expirationDate!);
    const oneYearAgo = new Date();
    oneYearAgo.setFullYear(oneYearAgo.getFullYear() - 1);
    return expirationDate <= oneYearAgo;
  }

  getCurrentApprovedLicense(): IHemoTechLicense | null {
    const approvedLicense = this.licenses.find(l => l.statuses.some(s => s.label === this.sharedService.approved));
    return approvedLicense ?? null;
  }

  cancelLicense(license: IHemoTechLicense): void {
    this.sharedService.openConfirm("Cancel this application? This action cannot be undone and will delete the application and all associated data.");
    this.sharedService.confirmed().pipe(take(1))
    .subscribe(confirmed => {
      if (!confirmed) return;

      this.licenseService.cancelLicense(license.id).subscribe({
        complete: () => {
          this.sharedService.toastr.success('Application canceled successfully.');
          this.licenses = this.licenses.filter(l => l.id !== license.id);
        },
        error: e => {
          console.log('error canceling application', e);
          this.sharedService.toastr.error('Error canceling application');
        }
      });
    });
  }

  openPDFDialog(license: IHemoTechLicense): void {
    this.dialog.open(HemoTechLicenseExportComponent, {
      data: license,
      role: 'dialog',
      ariaLabel: "License PDF Dialog",
      minWidth: '750px',
      maxWidth: '750px'
    });
  }

  canCancel(license: IHemoTechLicense): boolean {
    return license.statuses.some(s => s.label === this.sharedService.started);
  }

  canSelect(license: IHemoTechLicense): boolean {
    const latestStatus = license.statuses && license.statuses.length > 0
      ? license.statuses[license.statuses.length - 1]
      : null;
    const isExpired = latestStatus?.label === 'Expired';
    return !this.locked && !this.editing && !isExpired;
  }

  canRenew(license: IHemoTechLicense): boolean {
    var isApproved = license.statuses.some(s => s.label === this.sharedService.approved);
    if(!isApproved) return false;

    if(this.hasRenewal) return false;

    const expirationDate = new Date(license.expirationDate!);
    let expirationDateWindow = new Date();
    let yearAgo = expirationDate.getFullYear() - 1;
    expirationDateWindow.setFullYear(yearAgo);

    if(expirationDate <= expirationDateWindow) return false;

    expirationDateWindow = new Date();

    if(license.full){
      let expirationWindow = expirationDateWindow.getDate() + 90;
      expirationDateWindow.setDate(expirationWindow);
      return expirationDate <= expirationDateWindow;
    }
    else{
      if(license.renewalCount > 0){
        return false;
      }
      let expirationWindow = expirationDateWindow.getDate() + 60;
      expirationDateWindow.setDate(expirationWindow);
      return expirationDate <= expirationDateWindow;
    }
  }

  canRenewToFull(license: IHemoTechLicense): boolean {
    if(license.full) return false;
    if(this.hasRenewal) return false;
    if(this.expiredGreaterThanAYear(license)) return false;
    const isApproved = license.statuses.some(s => s.label === this.sharedService.approved);
    const isExpired = license.statuses.some(s => s.label === this.sharedService.expired);
    return (isApproved || isExpired);
  }

  canCreateLicense(): boolean {
    if(this.licenses.length === 0) return true;

    const oneYearAgo = new Date();
    oneYearAgo.setFullYear(oneYearAgo.getFullYear() - 1);

    return this.licenses.some(license => {
      const expirationDate = new Date(license.expirationDate!);
      return license.expirationDate !== null && expirationDate <= oneYearAgo;
    });
  }

  calculateFee(): number {
    let currentLicense: IHemoTechLicense = this.license.isRenewal ? this.getCurrentApprovedLicense()! : this.license;

    const expirationDate = new Date(currentLicense.expirationDate!);
    const threeWeeksBeforeExpiration = new Date(expirationDate.getTime() - 21 * 24 * 60 * 60 * 1000);

    let isLate = this.sharedService.today >= threeWeeksBeforeExpiration;

    let fee: number = 0;
    if(isLate && (this.approved || this.license.isRenewal)){
      fee += 25;
    }

    if(!this.license.full){
      return fee += 37.50;
    }

    if(this.license.full && currentLicense.full && this.license.isRenewal){
      return fee + 150;
    }

    const now = this.sharedService.today;
    const isAfterJuly = now.getMonth() >= 6;
    const isOddYear = now.getFullYear() % 2 === 1;

    if (isAfterJuly && isOddYear) {
      return fee += 150;
    }
    if(!isAfterJuly && !isOddYear){
      return fee += 112.50;
    }
    if(isAfterJuly && !isOddYear){
      return fee += 75;
    }
    if(!isAfterJuly && isOddYear){
      return fee += 37.50;
    }

    return fee;
  }

  clearValidationErrors(): void {
    this.validationErrors = [];
  }

  validateEmployers(): void {
    this.validationErrors.push(...this.employers.validateEmploymentHistory());
  }

  validateApplicationInformation(): void {
    this.validationErrors.push(...this.applicantInfo.validateApplicantInformation());
    this.validationErrors.push(...this.applicantInfo.validateAddresses());
  }

  validateQuestionsComponent(): void {
    this.validationErrors.push(...this.questions.validateQuestionsAndComments());
  }

  validateCertification(): void {
    this.validationErrors.push(...this.certification.validateNationalCertification());
    this.validationErrors.push(...this.certification.validateDocumentUploads());
  }

  onApplicantInfoValidityChange(isValid: boolean) {
    this.applicantInfoValid = isValid;
  }

  onEmployerInfoValidityChange(isValid: boolean) {
    this.employerInfoValid = isValid;
  }

  onQuestionsValidityChange(isValid: boolean) {
    this.questionsValid = isValid;
  }

  onCertificationValidityChange(isValid: boolean) {
    this.certificationValid = isValid;
  }

  setAsStarted(licenseId: number): void {
    this.licenseService.setAsStarted(licenseId).subscribe({
      next: (response) => {
        if(response){
          this.sharedService.toastr.success('License set as started');
          this.getLicenses();
        }
      },
      error: (e) => {
        console.error('Error setting license as started', e);
      }
    });
  }

  setExpirationDate(license: IHemoTechLicense): void {
    const expirationDate = this.expirationDates[license.id]?.toDateString() ?? '';
    this.licenseService.setExpirationDate(license.id, expirationDate).subscribe({
      next: (response) => {
        if(response){
          license.expirationDate = expirationDate;
          this.sharedService.toastr.success('Expiration date updated');
        }
      },
      error: (e) => {
        console.error('Error setting expiration date', e);
      },
      complete: () => {
        this.expirationDates[license.id] = null;
      }
    });
  }
}
